import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Brain, CheckCircle, XCircle, Loader } from 'lucide-react';
import { BASE_URL } from '../config/api';

const AccountValidationPage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isSubscribed = true;  // Add flag to handle cleanup

    const validateAccount = async () => {
      // If validation already in progress or completed, don't make another request
      if (status !== 'loading') return;

      console.log(`Validating account with UUID: ${uuid}`);
      try {
        console.log(`Making PUT request to: ${BASE_URL}/api/auth/validate/${uuid}`);
        const response = await fetch(`${BASE_URL}/api/auth/validate/${uuid}`, {
          method: 'PUT'
        });

        if (!isSubscribed) return;  // Don't update state if component unmounted

        if (response.ok) {
          console.log('Account validation successful');
          setStatus('success');
        } else {
          const data = await response.json();
          console.error('Validation failed:', data);
          setErrorMessage(data.message || 'Validation failed');
          setStatus('error');
        }
      } catch (error) {
        if (!isSubscribed) return;  // Don't update state if component unmounted
        
        console.error('Validation error:', error);
        setErrorMessage('Network error. Please try again later.');
        setStatus('error');
      }
    };

    validateAccount();

    // Cleanup function
    return () => {
      isSubscribed = false;
    };
  }, [uuid]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <Loader className="h-16 w-16 text-yellow-400 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold mb-4">Se validează contul tău...</h1>
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Cont validat cu succes!</h1>
            <p className="text-white/70 mb-6">
              Te rugăm să aștepți ca un administrator să îți activeze contul. Vei primi un email când contul tău va fi activat.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-6 rounded-lg transition"
            >
              Înapoi Acasă
            </button>
          </>
        );
      case 'error':
        return (
          <>
            <XCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-4">Validare eșuată</h1>
            <p className="text-red-300 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-bold py-2 px-6 rounded-lg transition"
            >
              Înapoi Acasă
            </button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-indigo-500 text-white">
      <header className="container mx-auto py-6 px-4">
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        >
          <Brain className="h-8 w-8" />
          <span className="text-2xl font-bold">PMind</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-20">
        <div className="max-w-md mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 text-center">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountValidationPage;
